import { Injectable } from "@angular/core";
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { DistinctChars, HasDigit, HasLetterOrDigit, HasLower, HasUpper } from "../helper/helper.function";
import moment from "moment";

@Injectable()
export class FormValidators {
    /**
   * @Description
   * Password validator
   * - min length
   * - upper case and lower case
   * - symbol
   */
    static CustomPasswordValidator = {
        RequiredLength: (minLength: number) => {
            return Validators.minLength(minLength);
        },
        RequireNonAlphanumeric: (error?: string) => {
            return (control: AbstractControl): ValidationErrors | null => {
                if (Validators.required(control) !== undefined && Validators.required(control) !== null) {
                    return null;
                }

                return HasLetterOrDigit(control.value)
                    ? { "RequireNonAlphanumeric": { "invalid": true, "error": error } }
                    : null;
            };
        },
        RequireDigit: (error?: string) => {
            return (control: AbstractControl): ValidationErrors | null => {
                if (Validators.required(control) !== undefined && Validators.required(control) !== null) {
                    return null;
                }

                return HasDigit(control.value)
                    ? null
                    : { "RequireDigit": { "invalid": true, "error": error } };
            };
        },
        RequireLowercase: (error?: string) => {
            return (control: AbstractControl): ValidationErrors | null => {
                if (Validators.required(control) !== undefined && Validators.required(control) !== null) {
                    return null;
                }

                return HasLower(control.value)
                    ? null
                    : { "RequireLowercase": { "invalid": true, "error": error } };
            };
        },
        RequireUppercase: (error?: string) => {
            return (control: AbstractControl): ValidationErrors | null => {
                if (Validators.required(control) !== undefined && Validators.required(control) !== null) {
                    return null;
                }

                return HasUpper(control.value)
                    ? null
                    : { "RequireUppercase": { "invalid": true, "error": error } };
            };
        },
        RequiredUniqueChars: (requiredCharCount: number, error?: string) => {
            return (control: AbstractControl): ValidationErrors | null => {
                if (Validators.required(control) !== undefined && Validators.required(control) !== null) {
                    return null;
                }

                const uniqueCount = DistinctChars(control.value).length;
                return uniqueCount < requiredCharCount
                    ? { "RequiredUniqueChars": { "invalid": true, "minChar": requiredCharCount, "actual": uniqueCount, "error": error } }
                    : null;
            };
        },
        GetErrorMessage: (control: AbstractControl, errorMessages: any[]) => {
            if (control.hasError("required")) {
                return "{0} is required.";
            }
            if (control.hasError("minlength")) {
                return errorMessages.find(x =>
                    x.code == "PasswordTooShort").description.replace("'{0}'", control.errors.minlength.requiredLength
                    );
            }
            if (control.hasError("RequireDigit")) {
                return errorMessages.find(x => x.code == "PasswordRequiresDigit").description;
            }
            if (control.hasError("RequireLowercase")) {
                return errorMessages.find(x => x.code == "PasswordRequiresLower").description;
            }
            if (control.hasError("RequireUppercase")) {
                return errorMessages.find(x => x.code == "PasswordRequiresUpper").description;
            }
            if (control.hasError("RequireNonAlphanumeric")) {
                return errorMessages.find(x => x.code == "PasswordRequiresNonAlphanumeric").description;
            }
            if (control.hasError("RequiredUniqueChars")) {
                return errorMessages.find(x =>
                    x.code == "PasswordRequiresUniqueChars").description.replace("'{0}'", control.errors.RequiredUniqueChars.minChar
                    );
            }

            return "";
        }
    };

    static isTodayDateValidator(date: Date, field: string): ValidatorFn {
        return (control: FormControl) => {
            let iscurrentDate = moment(date).isSame(new Date(), "day");
            return !iscurrentDate ? null : { invalidDate: `${field}` };
        };
    }

    static isLesserThanValidator(targetValue: number, errorMsg: string): ValidatorFn {
        return (control: FormControl) => {
            return control.value > targetValue ? { invalidValue: `${errorMsg}` } : null;
        };
    }


    static isDigit(errorMsg: string): ValidatorFn {
        return (control: FormControl) => {
            const regex = new RegExp("^[0-9]*$");
            const valid = regex.test(control.value);
            return valid ? null : { invalidPassword: `${errorMsg}` };
        };
    }

    static TargetDateGreaterThanSourceDateValidator(sourceDate: Date, targetDate: Date, errorMsg: string): ValidatorFn {
        return (control: FormControl) => {
            let tempCurrentDate = targetDate.setHours(0, 0, 0, 0);
            let tempPreviousDate = sourceDate.setHours(0, 0, 0, 0);
            return tempCurrentDate > tempPreviousDate ? null : { invalidDate: `${errorMsg}` };
        };
    }

    /**
     * @fileSizeLimit
     * File size default limit is 20mb
     */
    static fileSizeValidator(fileSizeLimit: number = 20,fileAsControl:boolean=true): ValidatorFn {
        return (control: FormControl) => {
            const filesize = ((fileAsControl ? control.value.size : control.value) / (1024*1024));
            return filesize < fileSizeLimit ? null : { invalidFileSize: `File size cannot exeed ${fileSizeLimit}MB` };
        };
    }

    /**
   * @Description
   * Video type validator only allow mp4 type
   */
    static videoTypeValidator(control: FormControl) {
        if (control.value.type.includes("video/")) {
            if (!control.value.type.includes("mp4") && !control.value.type.includes("quicktime")) {
                return { invalidFileType: `Invalid file type, only allow mp4 and mov ` };
            }
        }
        return null;
    }

    static imageTypeValidator(control: FormControl){
        if(!control.value.type.includes("image/")){
            return {invalidFileType:'Invalid file type, only allow image type'}
        }
        return null
    }

    /**
     * @Description
     * Audio and Video is not allow
     */
    static notAllowMediaTypeValidator(control: FormControl) {
        if (control.value.type.includes("video/") || control.value.type.includes("audio/")) {
            return { invalidFileType: `Media file type is not allow.` };
        }
        return null;
    }

}
